import { captureSentryError } from '~/exceptions/sentry';
import { useTranslation } from 'react-i18next';
import { useToast } from './useToast';
import { useCallback } from 'react';

type SentryContext = Parameters<typeof captureSentryError>[1];

interface IAsyncWithErrorToastOptions {
  source: string;
  tags: SentryContext['tags'];
}

// Wraps an async action with the standard refresh-fail UX:
// log to Sentry + surface a user-facing toast. Use for user-triggered
// actions where silent failure is unacceptable (Refresh buttons, etc.).
// Skip this helper when the call site needs different recovery —
// e.g. make-winner fails closed without a toast.
export const useAsyncWithErrorToast = () => {
  const { shopifyToast } = useToast();
  const { t } = useTranslation();

  return useCallback(
    async (
      fn: () => Promise<unknown>,
      { source, tags }: IAsyncWithErrorToastOptions,
    ): Promise<void> => {
      try {
        await fn();
      } catch (error) {
        captureSentryError(error, { source, level: 'warning', tags });
        shopifyToast(t('Refresh failed, please try again'), { isError: true });
      }
    },
    [shopifyToast, t],
  );
};
